import * as PIXI from 'pixi.js';
import * as PIXIFilters from 'pixi-filters';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import { BACKGROUND_REFLECTION_TYPE, CONSTANTS } from '../../PreviewDisplayConstants';
import { ninetyDegreeTurnsToRadians } from '../../helpers';
import { ASPECT_RATIO } from '../../../CreatePageConstants';
import { mediaSourceDimensions } from './MediaSourceDimensionTracker';
import { HslAdjustmentFilter } from '../../filters/hueRotationFilter.js/HslAdjustmentFilter';
import { DirectionalMirrorFilter } from '../../filters/reflection/directionalMirror/DirectionalMirrorFilter';
import { QuadrantMirrorFilter } from '../../filters/reflection/quadrantMirror/QuadrantMirrorFilter';
import DriftEffect from '../../effects/drift/DriftEffect';
import { getEffectiveDrift, BACKGROUND_ANCHORS } from '../../effects/drift/driftUtilities';

export default class ReactiveMedia {
    constructor(stage, zIndex) {
        this.stage = stage;

        this.sprite = new PIXI.Sprite();
        this.stage.addChild(this.sprite);

        this.width = CONSTANTS.DEFAULT_WIDTH;
        this.height = CONSTANTS.DEFAULT_HEIGHT;
        this.x = CONSTANTS.DEFAULT_MID_X;
        this.y = CONSTANTS.DEFAULT_MID_Y;

        this.offset = { x: 0, y: 0 };
        this.sourceDimensions = null;
        this.sprite.filters = [];
        this.drift = new DriftEffect();
        this.sprite.anchor.set(0.5);

        this.slowZoomEnabled = false;
        this.slowZoomIntensity = 30;
        this.slowZoomPhase = 0;
        this.slowZoomLastTime = 0;

        this.directionalMirrorFilter = new DirectionalMirrorFilter();
        this.directionalMirrorFilter.enabled = false;
        this.sprite.filters.push(this.directionalMirrorFilter);

        this.quadrantMirrorFilter = new QuadrantMirrorFilter();
        this.quadrantMirrorFilter.enabled = false;
        this.sprite.filters.push(this.quadrantMirrorFilter);

        this.hslFilter = new HslAdjustmentFilter();
        this.sprite.filters.push(this.hslFilter);

        this.crtFilter = new PIXIFilters.CRTFilter({
            curvature: 0,
            lineWidth: 0,
            lineContrast: 0,
            noise: 0,
            noiseSize: 1.0,
            vignetting: 0.0,
        });
        this.sprite.filters.push(this.crtFilter);

        this.adjustmentFilter = new PIXIFilters.AdjustmentFilter();
        this.sprite.filters.push(this.adjustmentFilter);

        this.zoomBlurFilter = new PIXIFilters.ZoomBlurFilter(
            0,
            [this.x, this.y],
            this.height / 2
        );
        this.sprite.filters.push(this.zoomBlurFilter);

        this.sprite.x = this.x;
        this.sprite.y = this.y;
        this.sprite.zIndex = zIndex;

        this.zoomBlurFilter.center = [this.x, this.y];
        this.zoomBlurFilter.innerRadius = 0;
    }

    updateHslAdjustmentFilter(options) {
        if (this.hslFilter.enabled !== options.enabled)
            this.hslFilter.enabled = options.enabled;

        if (this.hslFilter.colorize !== options.colorize)
            this.hslFilter.colorize = options.colorize;

        if (this.hslFilter.saturation !== options.saturation)
            this.hslFilter.saturation = options.saturation;

        if (this.hslFilter.lightness !== options.lightness)
            this.hslFilter.lightness = options.lightness;

        if (this.hslFilter.alpha !== options.alpha)
            this.hslFilter.alpha = options.alpha;

        if (this.hslFilter.hue !== options.hue)
            this.hslFilter.hue = options.hue;
    }

    updateReflectionFilterSettings(direction, type) {
        switch (type) {
            case BACKGROUND_REFLECTION_TYPE.TWO_WAY:
                this.directionalMirrorFilter.direction = direction;
                this.directionalMirrorFilter.enabled = true;
                this.quadrantMirrorFilter.enabled = false;
                break;
            case BACKGROUND_REFLECTION_TYPE.FOUR_WAY:
                this.quadrantMirrorFilter.direction = direction;
                this.quadrantMirrorFilter.enabled = true;
                this.directionalMirrorFilter.enabled = false;
                break;
            default:
                this.directionalMirrorFilter.enabled = false;
                this.quadrantMirrorFilter.enabled = false;
                break;
        }
    }

    update(config) {
        this.config = config;
        this.paddingFactor = config.shakeEnabled ? config.paddingFactor : 1;
        this.bounceEnabled = config.bounceEnabled;
        this.bounceScale = config.bounceScale;
        this.shakeEnabled = config.shakeEnabled;
        this.shakeFactor = config.shakeFactor;
        this.spectrumOptions = config.spectrumOptions;
        this.vignetteEnabled = config.vignetteEnabled;
        this.maxVignette = config.maxVignette;
        this.vignetteFactor = config.vignetteFactor;
        this.contrastEnabled = config.contrastEnabled;
        this.maxContrast = config.maxContrast;
        this.contrastFactor = config.contrastFactor;
        this.zoomBlurEnabled = config.zoomBlurEnabled;
        this.maxZoomBlur = config.maxZoomBlur;
        this.zoomBlurFactor = config.zoomBlurFactor;
        this.rotation = config.rotation;
        this.aspectRatio = config.aspectRatio;
        this.slowZoomEnabled = config.slowZoom?.enabled || false;
        this.slowZoomIntensity = config.slowZoom?.intensity ?? 30;

        if (this.reflectionType !== config.reflectionType || this.reflectionDirection !== config.reflectionDirection) {
            this.updateReflectionFilterSettings(config.reflectionDirection, config.reflectionType);
            this.reflectionDirection = config.reflectionDirection;
            this.reflectionType = config.reflectionType;
        }

        this.updateHslAdjustmentFilter(config.hlsAdjustmentSettings);

        let targetDimensions = mediaSourceDimensions[config.mediaSource.data?.url] || this.sourceDimensions;
        if (targetDimensions)
            this.correctAspectRatio(targetDimensions.width, targetDimensions.height);

        this.updateTransform(config);

        // When drift is enabled, draw() handles position/scale via applyTransform.
        // Only reset position/dimensions here when drift is disabled.
        const driftEnabled = this.drift && this.drift.enabled;

        if (!config.shakeEnabled && !driftEnabled) {
            this.sprite.x = this.x;
            this.sprite.y = this.y;
        }
        if (!config.bounceEnabled && !driftEnabled) {
            this.sprite.width = this.width;
            this.sprite.height = this.height;
        }
        if (!config.zoomBlurEnabled) {
            this.zoomBlurFilter.strength = 0
        }
        if (!config.contrastEnabled) {
            this.adjustmentFilter.contrast = 1;
        }
        if (!config.vignetteEnabled) {
            this.crtFilter.vignetting = 0;
        }
        if (!this.slowZoomEnabled) {
            this.slowZoomPhase = 0;
            this.slowZoomLastTime = 0;
        }
    }

    draw(spectrum) {
        const processedSpectrum = ProcessSpectrum(spectrum, this.spectrumOptions);
        const spectrumMagnitude = GetSpectrumMagnitude(processedSpectrum);

        if (this.slowZoomEnabled) {
            const now = performance.now();
            const dt = (this.slowZoomLastTime && (now - this.slowZoomLastTime) < 1000)
                ? (now - this.slowZoomLastTime) / 1000
                : 1 / 30;
            this.slowZoomLastTime = now;
            const speedHz = 0.02 + (this.slowZoomIntensity / 100) * 0.18;
            this.slowZoomPhase += dt * speedHz * Math.PI * 2;
            const slowZoomScale = 1.06 + 0.06 * Math.sin(this.slowZoomPhase);
            if (this.drift && this.drift.enabled) {
                const transform = this.drift.compute(spectrumMagnitude);
                if (transform) {
                    this.applyTransform(
                        this.x + transform.translateX,
                        this.y + transform.translateY,
                        transform.scale * slowZoomScale,
                        transform.rotation
                    );
                }
            } else {
                this.sprite.x = this.x;
                this.sprite.y = this.y;
                this.sprite.width = this.width * slowZoomScale;
                this.sprite.height = this.height * slowZoomScale;
                this.sprite.rotation = ninetyDegreeTurnsToRadians(this.rotation);
            }
        } else if (this.drift && this.drift.enabled) {
            const transform = this.drift.compute(spectrumMagnitude);
            if (transform) {
                this.applyTransform(this.x + transform.translateX, this.y + transform.translateY, transform.scale, transform.rotation);
            }
        } else {
            if (this.bounceEnabled) {
                this.correctPaddingFactor(spectrumMagnitude);
            }
            if (this.shakeEnabled) {
                let newXOffset = (this.offset.x + (Math.random() - 0.5) * this.shakeFactor) * (spectrumMagnitude / 255);
                const offsetXOvershot = Math.abs(newXOffset) - (this.sprite.width - this.width) / 2;
                if (offsetXOvershot > 0) {
                    newXOffset -= Math.sign(newXOffset) * offsetXOvershot;
                }
                let newYOffset = (this.offset.y + (Math.random() - 0.5) * this.shakeFactor) * (spectrumMagnitude / 255);
                const offsetYOvershot = Math.abs(newYOffset) - (this.sprite.height - this.height) / 2;
                if (offsetYOvershot > 0) {
                    newYOffset -= Math.sign(newYOffset) * offsetYOvershot;
                }
                this.offset = {
                    x: newXOffset,
                    y: newYOffset
                }
                this.sprite.x = this.x + newXOffset;
                this.sprite.y = this.y + newYOffset;
            }
        }

        if (this.vignetteEnabled) {
            const newVignetting = (spectrumMagnitude / 255) * this.vignetteFactor;
            this.crtFilter.vignetting = newVignetting > this.maxVignette ? this.maxVignette : newVignetting;
        }
        if (this.contrastEnabled) {
            const newContrast = 1 + (spectrumMagnitude / 255) * this.contrastFactor;
            this.adjustmentFilter.contrast = newContrast > this.maxContrast ? this.maxContrast : newContrast;
        }
        if (this.zoomBlurEnabled) {
            const newZoomBlur = (spectrumMagnitude / 255) * this.zoomBlurFactor;
            this.zoomBlurFilter.strength = newZoomBlur > this.maxZoomBlur ? this.maxZoomBlur : newZoomBlur;
        }
    }

    refreshAfterSourceDimensionsChange() {
        const driftEnabled = this.drift && this.drift.enabled;

        // Reset transient motion state and drift timeline
        this.offset = { x: 0, y: 0 };
        this.drift.reset();

        // Re-apply drift configuration and transform using the latest width/height.
        // This will update the sprite dimensions and rotation via updateTransform().
        if (this.config)
            this.updateTransform(this.config);

        // If bounce is enabled but drift is not, we need to apply the padding factor separately
        // as bounce calculation logic (correctPaddingFactor) is not part of updateTransform.
        if (!driftEnabled && this.bounceEnabled) {
            this.correctPaddingFactor();
        }
    }

    updateStage(stage) {
        this.stage = stage;
        this.stage.addChild(this.sprite);
    }

    destroy() {
        if (this.sprite) {
            if (this.sprite.parent) {
                this.sprite.parent.removeChild(this.sprite);
            }
            this.sprite.filters = null;
            this.sprite.destroy({ children: true, texture: false, baseTexture: false });
            this.sprite = null;
        }
        if (this.drift && typeof this.drift.destroy === 'function') {
            this.drift.destroy();
        }
    }

    correctPaddingFactor(spectrumMagnitude = 0) {
        const scaleMultiplier = this.paddingFactor * (1 + (spectrumMagnitude / 255) * this.bounceScale);
        let newWidth = this.width * scaleMultiplier;
        let newHeight = this.height * scaleMultiplier;
        this.sprite.width = newWidth < this.width ? this.width : newWidth;
        this.sprite.height = newHeight < this.height ? this.height : newHeight;
    }

    correctAspectRatio(sourceWidth, sourceHeight) {
        if (!sourceWidth || !sourceHeight)
            return;

        this.sourceDimensions = { width: sourceWidth, height: sourceHeight };

        const isSideways = this.rotation % 2 !== 0;
        const sourceFactor = isSideways ? (sourceHeight / sourceWidth) : (sourceWidth / sourceHeight);

        let aspectRatioFactor = 1.7777777;
        if (this.aspectRatio === ASPECT_RATIO.PORTRAIT)
            aspectRatioFactor = 0.5625;
        if (this.aspectRatio === ASPECT_RATIO.SQUARE)
            aspectRatioFactor = 1;

        let newWidth = CONSTANTS.DEFAULT_WIDTH;
        let newHeight = CONSTANTS.DEFAULT_HEIGHT;

        if (sourceFactor >= aspectRatioFactor) {
            newWidth = CONSTANTS.DEFAULT_WIDTH * (sourceFactor / aspectRatioFactor);
            newHeight = CONSTANTS.DEFAULT_HEIGHT;
        } else {
            newWidth = CONSTANTS.DEFAULT_WIDTH;
            newHeight = CONSTANTS.DEFAULT_HEIGHT * (aspectRatioFactor / sourceFactor);
        }

        if (isSideways) {
            this.width = newHeight;
            this.height = newWidth;
        } else {
            this.width = newWidth;
            this.height = newHeight;
        }

        this.sprite.width = this.width;
        this.sprite.height = this.height;
    }

    updateTransform(config) {
        // Set base transform first so baseRotation is available when updateConfig
        // triggers _updateEdgeCoverScale (which uses baseRotation for correct scale).
        this.drift.setBaseTransform(
            { x: this.x, y: this.y },
            ninetyDegreeTurnsToRadians(config.rotation),
            { x: 1, y: 1 }
        );

        // Resolve effective drift (preset from intensity or custom values)
        const effectiveDrift = getEffectiveDrift(config.drift, BACKGROUND_ANCHORS);
        this.drift.updateConfig(effectiveDrift);

        this.drift.setDimensions(this.width, this.height);

        const driftEnabled = this.drift && this.drift.enabled;

        if (driftEnabled) {
            // Apply initial/current drift transform to ensure correct aspect ratio and edge cover in preview.
            // When not playing, compute() will use 0 for spectrum magnitude and performance.now() for time.
            const transform = this.drift.compute(0);
            if (transform) {
                this.applyTransform(this.x + transform.translateX, this.y + transform.translateY, transform.scale, transform.rotation);
                return;
            }
        }

        // Apply base dimensions and rotation when drift is disabled or returns no transform.
        this.sprite.width = this.width;
        this.sprite.height = this.height;
        this.sprite.rotation = ninetyDegreeTurnsToRadians(config.rotation);
        this.sprite.x = this.x;
        this.sprite.y = this.y;
    }

    applyTransform(x, y, scale, rotation) {
        this.sprite.x = x;
        this.sprite.y = y;
        this.sprite.width = this.width * scale;
        this.sprite.height = this.height * scale;
        // Add base rotation so drift rotation is applied on top of element's base rotation
        this.sprite.rotation = ninetyDegreeTurnsToRadians(this.rotation) + rotation;
    }
}